import React, { useCallback, useEffect, useRef, useState } from "react";

import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  LayerGroup,
  LayersControl,
  MapContainer as LeafletMapContainer,
  Marker,
  Pane,
  Polyline,
  TileLayer,
  useMap,
  useMapEvents,
  WMSTileLayer,
} from "react-leaflet";
import { useTranslation } from "react-i18next";

import MapMarkers from "./MapMarkers";
import {
  CLOSE_MAP_TOOLS_PANELS_EVENT,
  MAP_TOOLS_PANEL_STATE_EVENT,
  SUPPRESS_NEXT_MAP_TOOLS_TOGGLE_EVENT,
  TOGGLE_MAP_LAYERS_EVENT,
  TOGGLE_MAP_MEASURE_EVENT,
} from "./mapToolsEvents";

const { BaseLayer, Overlay } = LayersControl;
const BATHYMETRY_LAYER_KEYS = {
  gebcoColours: "gebcoColours",
  gebcoRelief: "gebcoRelief",
  noaaContours: "noaaContours",
  emodnetColours: "emodnetColours",
  emodnetContours: "emodnetContours",
};
const GEBCO_COLOURS_LAYER_NAME = "Global bathymetry colours / GEBCO";
const GEBCO_RELIEF_LAYER_NAME = "Global bathymetry relief / GEBCO";
const NOAA_CONTOURS_LAYER_NAME = "Global depth contours / NOAA-NCEI GEBCO";
const EMODNET_COLOURS_LAYER_NAME = "Europe/UK bathymetry colours / EMODnet";
const EMODNET_CONTOURS_LAYER_NAME = "Europe/UK depth contours / EMODnet";
const BATHYMETRY_WARNING =
  "Approximate seabed depth. Not live, not tide-adjusted, not for navigation.";
const EMODNET_ATTRIBUTION_SOURCE =
  "EMODnet Bathymetry / European Commission, CC BY 4.0";
const GEBCO_ATTRIBUTION_SOURCE = "GEBCO Grid";
const NOAA_GEBCO_CONTOURS_ATTRIBUTION_SOURCE =
  "NOAA NCEI / GEBCO contours";
const LEAFLET_MAP_TEST_HOOK = "__REELITPRO_LEAFLET_MAP__";
const PRODUCTION_HOSTNAMES = new Set(["reelitpro.com", "www.reelitpro.com"]);
const TEST_HOOK_HOSTNAMES = new Set([
  "localhost",
  "127.0.0.1",
  "::1",
  "staging.reelitpro.com",
]);
const TEST_HOOK_DEPLOY_ENVS = new Set(["development", "dev", "local", "staging", "test"]);
const MAP_LAYER_PREVIEW_BASE = "/images/map-layer-previews";
const MAP_TOOL_INTERACTIVE_SELECTOR = [
  ".map-filter-panel",
  ".map-filter-toggle",
  ".map-tools",
  ".map-layers-sheet",
  ".map-measure-control",
  ".map-location-permission-dialog",
  ".leaflet-control-layers",
].join(",");

function formatMeasuredDistance(distanceMeters) {
  if (!Number.isFinite(distanceMeters)) {
    return "";
  }

  if (distanceMeters < 1000) {
    return `${Math.round(distanceMeters)} m`;
  }

  return `${(distanceMeters / 1000).toFixed(2)} km`;
}

function stopMapControlEvent(event) {
  event.preventDefault();
  event.stopPropagation();
  event.nativeEvent?.stopImmediatePropagation?.();
}

function stopMapControlPointerEvent(event) {
  event.stopPropagation();
  event.nativeEvent?.stopImmediatePropagation?.();
}

function suppressNextMapToolsToggle() {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new Event(SUPPRESS_NEXT_MAP_TOOLS_TOGGLE_EVENT));
}

function getBathymetryLayerKey(layerName) {
  switch (layerName) {
    case GEBCO_COLOURS_LAYER_NAME:
      return BATHYMETRY_LAYER_KEYS.gebcoColours;
    case GEBCO_RELIEF_LAYER_NAME:
      return BATHYMETRY_LAYER_KEYS.gebcoRelief;
    case NOAA_CONTOURS_LAYER_NAME:
      return BATHYMETRY_LAYER_KEYS.noaaContours;
    case EMODNET_COLOURS_LAYER_NAME:
      return BATHYMETRY_LAYER_KEYS.emodnetColours;
    case EMODNET_CONTOURS_LAYER_NAME:
      return BATHYMETRY_LAYER_KEYS.emodnetContours;
    default:
      return null;
  }
}

function getBathymetryAttribution(activeLayers) {
  const hasGebco = activeLayers.gebcoColours || activeLayers.gebcoRelief;
  const hasNoaaContours = activeLayers.noaaContours;
  const hasEmodnet =
    activeLayers.emodnetColours || activeLayers.emodnetContours;
  const sources = [];

  if (hasEmodnet) {
    sources.push(EMODNET_ATTRIBUTION_SOURCE);
  }

  if (hasGebco) {
    sources.push(GEBCO_ATTRIBUTION_SOURCE);
  }

  if (hasNoaaContours) {
    sources.push(NOAA_GEBCO_CONTOURS_ATTRIBUTION_SOURCE);
  }

  return sources.length ? `Bathymetry: ${sources.join("; ")}` : "";
}

function canExposeLeafletMapTestHook() {
  if (typeof window === "undefined" || !window.location) {
    return false;
  }

  const hostname = String(window.location.hostname || "").trim().toLowerCase();
  if (PRODUCTION_HOSTNAMES.has(hostname)) {
    return false;
  }

  if (TEST_HOOK_HOSTNAMES.has(hostname)) {
    return true;
  }

  const deployEnv = String(process.env.REACT_APP_DEPLOY_ENV || "")
    .trim()
    .toLowerCase();
  if (TEST_HOOK_DEPLOY_ENVS.has(deployEnv)) {
    return true;
  }

  return process.env.NODE_ENV !== "production";
}

function MapRefSetter({ mapRef }) {
  const map = useMap();

  useEffect(() => {
    if (map && !mapRef.current) {
      mapRef.current = map;
      console.log("Map instance captured via useMap");
    }

    if (!map || !canExposeLeafletMapTestHook()) {
      return undefined;
    }

    window[LEAFLET_MAP_TEST_HOOK] = map;

    return () => {
      if (window[LEAFLET_MAP_TEST_HOOK] === map) {
        delete window[LEAFLET_MAP_TEST_HOOK];
      }
    };
  }, [map, mapRef]);

  return null;
}

function MapLayerControlActivity({
  onBaseLayerChange,
  onBathymetryLayerActiveChange,
}) {
  useMapEvents({
    baselayerchange(event) {
      onBaseLayerChange(event?.name || "");
    },
    overlayadd(event) {
      const layerKey = getBathymetryLayerKey(event?.name);

      if (layerKey) {
        onBathymetryLayerActiveChange(layerKey, true);
      }
    },
    overlayremove(event) {
      const layerKey = getBathymetryLayerKey(event?.name);

      if (layerKey) {
        onBathymetryLayerActiveChange(layerKey, false);
      }
    },
  });

  return null;
}

function MobileMapLayersSheet({
  isOpen,
  onClose,
  baseLayers,
  overlayLayers,
  activeBaseLayer,
  activeBathymetryLayers,
  onSelectLayer,
}) {
  const { t } = useTranslation();
  const sheetRef = useRef(null);
  const getCoverageLabel = (coverage) =>
    coverage === "europe" ? "Europe / UK" : "Global";

  useEffect(() => {
    if (!sheetRef.current) {
      return;
    }

    L.DomEvent.disableClickPropagation(sheetRef.current);
    L.DomEvent.disableScrollPropagation(sheetRef.current);
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={sheetRef}
      className="map-layers-sheet"
      onClick={stopMapControlEvent}
      onDoubleClick={stopMapControlEvent}
      onContextMenu={stopMapControlEvent}
      onPointerDown={stopMapControlPointerEvent}
      onMouseDown={stopMapControlPointerEvent}
      onTouchStart={stopMapControlPointerEvent}
    >
      <div className="map-layers-sheet__head">
        <div>
          <span className="map-layers-sheet__handle" aria-hidden="true" />
          <h3>{t("map_tools_layers", { defaultValue: "Map Layers" })}</h3>
        </div>
        <button
          type="button"
          className="map-layers-sheet__close"
          onClick={onClose}
          aria-label={t("common_close", { defaultValue: "Close" })}
        >
          x
        </button>
      </div>

      <section className="map-layers-section">
        <div className="map-layers-section__title">
          {t("map_layers_base_title", { defaultValue: "Base map" })}
        </div>
        <div className="map-layers-grid map-layers-grid--base">
          {baseLayers.map((layer) => {
            const isActive = activeBaseLayer === layer.name;
            return (
              <button
                key={layer.key}
                type="button"
                className={`map-layer-card${isActive ? " is-active" : ""}`}
                onClick={() => onSelectLayer(layer.name)}
                aria-pressed={isActive}
              >
                <span className="map-layer-card__preview" aria-hidden="true">
                  <img src={layer.previewSrc} alt="" loading="lazy" decoding="async" />
                </span>
                <span className="map-layer-card__label">{layer.label}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="map-layers-section">
        <div className="map-layers-section__title">
          {t("map_layers_depth_title", { defaultValue: "Depth overlays" })}
        </div>
        <div className="map-layers-grid map-layers-grid--overlay">
          {overlayLayers.map((layer) => {
            const isActive = Boolean(activeBathymetryLayers[layer.layerKey]);
            return (
              <button
                key={layer.key}
                type="button"
                className={`map-layer-card map-layer-card--overlay${isActive ? " is-active" : ""}`}
                onClick={() => onSelectLayer(layer.name)}
                aria-pressed={isActive}
              >
                <span className="map-layer-card__preview" aria-hidden="true">
                  <img src={layer.previewSrc} alt="" loading="lazy" decoding="async" />
                </span>
                <span className="map-layer-card__label">{layer.label}</span>
                <span className="map-layer-card__meta">
                  {getCoverageLabel(layer.coverage)}
                </span>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function BathymetryAttribution({ attribution }) {
  const leafletMap = useMap();

  useEffect(() => {
    if (!attribution || !leafletMap.attributionControl) {
      return undefined;
    }

    leafletMap.attributionControl.addAttribution(attribution);

    return () => {
      leafletMap.attributionControl.removeAttribution(attribution);
    };
  }, [attribution, leafletMap]);

  return null;
}

function MapMeasurementTool({
  isMeasuring,
  measurementPoints,
  onToggleMeasuring,
  onClearMeasurement,
  onExitMeasuring,
}) {
  const { t } = useTranslation();
  const measureControlRef = useRef(null);
  const [pointA, pointB] = measurementPoints;
  const hasPointA = Boolean(pointA);
  const hasPointB = Boolean(pointB);
  const distanceLabel =
    hasPointA && hasPointB
      ? formatMeasuredDistance(L.latLng(pointA).distanceTo(pointB))
      : "";
  const instruction = !isMeasuring
    ? t("map_measure_distance", { defaultValue: "Measure distance" })
    : !hasPointA
    ? t("map_measure_tap_first_point", { defaultValue: "Tap first point" })
    : !hasPointB
    ? t("map_measure_tap_second_point", { defaultValue: "Tap second point" })
    : t("map_measure_total_distance", { defaultValue: "Total distance" });

  useEffect(() => {
    if (!measureControlRef.current) {
      return;
    }

    L.DomEvent.disableClickPropagation(measureControlRef.current);
    L.DomEvent.disableScrollPropagation(measureControlRef.current);
  }, []);

  const handleExitClick = (event) => {
    stopMapControlEvent(event);
    suppressNextMapToolsToggle();
    onExitMeasuring();
  };

  return (
    <>
      {hasPointA && <Marker position={[pointA.lat, pointA.lng]} />}
      {hasPointB && <Marker position={[pointB.lat, pointB.lng]} />}
      {hasPointA && hasPointB && (
        <Polyline
          positions={[
            [pointA.lat, pointA.lng],
            [pointB.lat, pointB.lng],
          ]}
          pathOptions={{
            color: "#0f766e",
            weight: 4,
            opacity: 0.9,
          }}
        />
      )}

      <div
        ref={measureControlRef}
        className={`map-measure-control${isMeasuring ? " is-measuring" : ""}`}
        aria-live="polite"
        onClick={stopMapControlEvent}
        onDoubleClick={stopMapControlEvent}
        onContextMenu={stopMapControlEvent}
        onPointerDown={stopMapControlPointerEvent}
        onMouseDown={stopMapControlPointerEvent}
        onTouchStart={stopMapControlPointerEvent}
      >
        <button
          type="button"
          className={`map-measure-toggle${isMeasuring ? " is-active" : ""}`}
          onClick={onToggleMeasuring}
          aria-pressed={isMeasuring}
          aria-label={t("map_measure_distance", {
            defaultValue: "Measure distance",
          })}
        >
          {t("map_measure_distance", { defaultValue: "Measure distance" })}
        </button>

        {isMeasuring && (
          <div className="map-measure-panel">
            <div className="map-measure-status">
              <span>{instruction}</span>
              {distanceLabel && (
                <strong className="map-measure-distance">{distanceLabel}</strong>
              )}
            </div>
            <div className="map-measure-actions">
              <button
                type="button"
                className="map-measure-action"
                onClick={onClearMeasurement}
                disabled={!hasPointA}
              >
                {t("map_measure_clear", { defaultValue: "Clear" })}
              </button>
              <button
                type="button"
                className="map-measure-action"
                onClick={handleExitClick}
              >
                {t("map_measure_exit", { defaultValue: "Exit" })}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png",
});

export default function MapInteractionLayer({ map }) {
  const { t } = useTranslation();
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [measurementPoints, setMeasurementPoints] = useState([]);
  const activeMapToolPanelsRef = useRef(new Set());
  const streetLayerName = t("map_layer_street");
  const satelliteLayerName = t("map_layer_satellite", {
    defaultValue: "Satellite",
  });
  const oceanLayerName = t("map_layer_ocean");
  const [activeBaseLayer, setActiveBaseLayer] = useState(streetLayerName);
  const [mobileLayersOpen, setMobileLayersOpen] = useState(false);
  const [activeBathymetryLayers, setActiveBathymetryLayers] = useState({
    gebcoColours: false,
    gebcoRelief: false,
    noaaContours: false,
    emodnetColours: false,
    emodnetContours: false,
  });
  const isBathymetryActive = Object.values(activeBathymetryLayers).some(Boolean);
  const bathymetryAttribution = getBathymetryAttribution(
    activeBathymetryLayers
  );

  const setBathymetryLayerActive = (layerKey, isActive) => {
    setActiveBathymetryLayers((currentLayers) => ({
      ...currentLayers,
      [layerKey]: isActive,
    }));
  };

  const baseLayerOptions = [
    {
      key: "street",
      name: streetLayerName,
      label: streetLayerName,
      previewSrc: `${MAP_LAYER_PREVIEW_BASE}/street-map.jpg`,
    },
    {
      key: "satellite",
      name: satelliteLayerName,
      label: satelliteLayerName,
      previewSrc: `${MAP_LAYER_PREVIEW_BASE}/satellite.jpg`,
    },
    {
      key: "ocean",
      name: oceanLayerName,
      label: oceanLayerName,
      previewSrc: `${MAP_LAYER_PREVIEW_BASE}/ocean-map.jpg`,
    },
  ];
  const overlayLayerOptions = [
    {
      key: "gebco-colours",
      layerKey: BATHYMETRY_LAYER_KEYS.gebcoColours,
      name: GEBCO_COLOURS_LAYER_NAME,
      label: t("map_layer_gebco_colours", {
        defaultValue: "GEBCO colours",
      }),
      coverage: "global",
      previewSrc: `${MAP_LAYER_PREVIEW_BASE}/gebco-colours.jpg`,
    },
    {
      key: "gebco-relief",
      layerKey: BATHYMETRY_LAYER_KEYS.gebcoRelief,
      name: GEBCO_RELIEF_LAYER_NAME,
      label: t("map_layer_gebco_relief", {
        defaultValue: "GEBCO relief",
      }),
      coverage: "global",
      previewSrc: `${MAP_LAYER_PREVIEW_BASE}/gebco-relief.jpg`,
    },
    {
      key: "noaa-contours",
      layerKey: BATHYMETRY_LAYER_KEYS.noaaContours,
      name: NOAA_CONTOURS_LAYER_NAME,
      label: t("map_layer_noaa_contours", {
        defaultValue: "NOAA contours",
      }),
      coverage: "global",
      previewSrc: `${MAP_LAYER_PREVIEW_BASE}/noaa-contours.jpg`,
    },
    {
      key: "emodnet-colours",
      layerKey: BATHYMETRY_LAYER_KEYS.emodnetColours,
      name: EMODNET_COLOURS_LAYER_NAME,
      label: t("map_layer_emodnet_colours", {
        defaultValue: "EMODnet colours",
      }),
      coverage: "europe",
      previewSrc: `${MAP_LAYER_PREVIEW_BASE}/emodnet-colours.jpg`,
    },
    {
      key: "emodnet-contours",
      layerKey: BATHYMETRY_LAYER_KEYS.emodnetContours,
      name: EMODNET_CONTOURS_LAYER_NAME,
      label: t("map_layer_emodnet_contours", {
        defaultValue: "EMODnet contours",
      }),
      coverage: "europe",
      previewSrc: `${MAP_LAYER_PREVIEW_BASE}/emodnet-contours.jpg`,
    },
  ];

  useEffect(() => {
    setActiveBaseLayer(streetLayerName);
  }, [streetLayerName]);

  const selectLeafletControlLayer = useCallback(
    (layerName) => {
      const normalizedLayerName = String(layerName || "").trim();
      const mapContainer = map.mapRef.current?.getContainer?.();
      const labels = Array.from(
        mapContainer?.querySelectorAll(".leaflet-control-layers label") || []
      );
      const targetLabel = labels.find(
        (label) => label.textContent?.trim() === normalizedLayerName
      );
      const input = targetLabel?.querySelector("input");

      input?.click();
    },
    [map.mapRef]
  );

  const clearMeasurement = () => {
    setMeasurementPoints([]);
  };

  const exitMeasuring = useCallback(() => {
    setIsMeasuring(false);
    setMeasurementPoints([]);
  }, []);

  const toggleMeasuring = useCallback(() => {
    setIsMeasuring((current) => {
      if (current) {
        setMeasurementPoints([]);
      }

      return !current;
    });
  }, []);

  useEffect(() => {
    const handleToggleMeasure = () => {
      toggleMeasuring();
    };

    window.addEventListener(TOGGLE_MAP_MEASURE_EVENT, handleToggleMeasure);
    return () => {
      window.removeEventListener(TOGGLE_MAP_MEASURE_EVENT, handleToggleMeasure);
    };
  }, [toggleMeasuring]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(MAP_TOOLS_PANEL_STATE_EVENT, {
        detail: {
          id: "measure",
          isOpen: isMeasuring,
          dismissOnMapClick: false,
        },
      })
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent(MAP_TOOLS_PANEL_STATE_EVENT, {
          detail: { id: "measure", isOpen: false },
        })
      );
    };
  }, [isMeasuring]);

  useEffect(() => {
    const handleToggleLayers = () => {
      setMobileLayersOpen(true);
    };

    window.addEventListener(TOGGLE_MAP_LAYERS_EVENT, handleToggleLayers);
    return () => {
      window.removeEventListener(TOGGLE_MAP_LAYERS_EVENT, handleToggleLayers);
    };
  }, []);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(MAP_TOOLS_PANEL_STATE_EVENT, {
        detail: { id: "layers", isOpen: mobileLayersOpen },
      })
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent(MAP_TOOLS_PANEL_STATE_EVENT, {
          detail: { id: "layers", isOpen: false },
        })
      );
    };
  }, [mobileLayersOpen]);

  useEffect(() => {
    const handleCloseMapToolsPanels = () => {
      setMobileLayersOpen(false);
      exitMeasuring();
    };

    window.addEventListener(
      CLOSE_MAP_TOOLS_PANELS_EVENT,
      handleCloseMapToolsPanels
    );
    return () => {
      window.removeEventListener(
        CLOSE_MAP_TOOLS_PANELS_EVENT,
        handleCloseMapToolsPanels
      );
    };
  }, [exitMeasuring]);

  const closeOpenMapToolPanels = useCallback(() => {
    if (activeMapToolPanelsRef.current.size === 0) {
      return false;
    }

    activeMapToolPanelsRef.current.clear();
    window.dispatchEvent(new Event(CLOSE_MAP_TOOLS_PANELS_EVENT));
    return true;
  }, []);

  useEffect(() => {
    const handleMapToolPanelState = (event) => {
      const panelId = event?.detail?.id;
      if (!panelId) {
        return;
      }

      if (event.detail.isOpen && event.detail.dismissOnMapClick !== false) {
        activeMapToolPanelsRef.current.add(panelId);
      } else {
        activeMapToolPanelsRef.current.delete(panelId);
      }
    };

    const handleDocumentClickCapture = (event) => {
      if (activeMapToolPanelsRef.current.size === 0) {
        return;
      }

      if (event.target?.closest?.(MAP_TOOL_INTERACTIVE_SELECTOR)) {
        return;
      }

      if (event.cancelable) {
        event.preventDefault();
      }
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      closeOpenMapToolPanels();
    };

    window.addEventListener(MAP_TOOLS_PANEL_STATE_EVENT, handleMapToolPanelState);
    document.addEventListener("click", handleDocumentClickCapture, true);
    return () => {
      window.removeEventListener(
        MAP_TOOLS_PANEL_STATE_EVENT,
        handleMapToolPanelState
      );
      document.removeEventListener("click", handleDocumentClickCapture, true);
    };
  }, [closeOpenMapToolPanels]);

  const handleMeasurementClick = useCallback((lat, lng) => {
    setMeasurementPoints((currentPoints) => {
      const nextPoint = { lat, lng };

      if (currentPoints.length === 0 || currentPoints.length >= 2) {
        return [nextPoint];
      }

      return [currentPoints[0], nextPoint];
    });
  }, []);

  const handleMapCanvasClick = useCallback(
    (lat, lng) => {
      if (closeOpenMapToolPanels()) {
        return;
      }

      if (isMeasuring) {
        handleMeasurementClick(lat, lng);
        return;
      }

      map.handleMapClick(lat, lng);
    },
    [closeOpenMapToolPanels, handleMeasurementClick, isMeasuring, map]
  );

  const handleBasinClick = useCallback(
    (basin) => {
      if (closeOpenMapToolPanels()) {
        return;
      }

      map.handleBasinClick(basin);
    },
    [closeOpenMapToolPanels, map]
  );

  const handleSpotClick = useCallback(
    (spot) => {
      if (closeOpenMapToolPanels()) {
        return;
      }

      map.handleSpotClick(spot);
    },
    [closeOpenMapToolPanels, map]
  );

  return (
    <LeafletMapContainer
      center={[20, 0]}
      zoom={3}
      minZoom={2}
      maxZoom={18}
      maxBounds={[[-85, -180], [85, 180]]}
      maxBoundsViscosity={1.0}
      noWrap={true}
      worldCopyJump={false}
      className="map-leaflet"
      whenCreated={(leafletMap) => {
        map.mapRef.current = leafletMap;
      }}
      whenReady={() => {
        map.setMapReady(true);
      }}
    >
      <Pane name="bathymetryPane" />

      <LayersControl position="topright">
        <BaseLayer checked name={streetLayerName}>
          <TileLayer
            url="https://services.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
            attribution="© Esri, OpenStreetMap contributors"
            noWrap={true}
            bounds={[[-85, -180], [85, 180]]}
            crossOrigin="anonymous"
          />
        </BaseLayer>

        <BaseLayer
          name={satelliteLayerName}
        >
          <TileLayer
            url="https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution="© Esri, Maxar, Earthstar Geographics, and the GIS User Community"
            noWrap={true}
            bounds={[[-85, -180], [85, 180]]}
            crossOrigin="anonymous"
          />
        </BaseLayer>

        <BaseLayer name={oceanLayerName}>
          <TileLayer
            url="https://services.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}"
            attribution="© Esri, NOAA"
            noWrap={true}
            bounds={[[-85, -180], [85, 180]]}
            crossOrigin="anonymous"
          />
        </BaseLayer>

        <Overlay name={GEBCO_COLOURS_LAYER_NAME}>
          <LayerGroup>
            <WMSTileLayer
              url="https://wms.gebco.net/mapserv"
              layers="GEBCO_LATEST_2"
              format="image/png"
              transparent={true}
              opacity={0.55}
              version="1.1.1"
              noWrap={true}
              bounds={[[-85, -180], [85, 180]]}
              crossOrigin="anonymous"
              pane="bathymetryPane"
              zIndex={352}
            />
          </LayerGroup>
        </Overlay>

        <Overlay name={GEBCO_RELIEF_LAYER_NAME}>
          <LayerGroup>
            <WMSTileLayer
              url="https://wms.gebco.net/mapserv"
              layers="GEBCO_LATEST"
              format="image/png"
              transparent={true}
              opacity={0.65}
              version="1.1.1"
              noWrap={true}
              bounds={[[-85, -180], [85, 180]]}
              crossOrigin="anonymous"
              pane="bathymetryPane"
              zIndex={351}
            />
          </LayerGroup>
        </Overlay>

        <Overlay name={NOAA_CONTOURS_LAYER_NAME}>
          <LayerGroup>
            <TileLayer
              url="https://tiles.arcgis.com/tiles/C8EMgrsFcRFL6LrL/arcgis/rest/services/GEBCO_contours/MapServer/tile/{z}/{y}/{x}"
              noWrap={true}
              bounds={[[-85, -180], [85, 180]]}
              pane="bathymetryPane"
              zIndex={353}
            />
          </LayerGroup>
        </Overlay>

        <Overlay name={EMODNET_COLOURS_LAYER_NAME}>
          <LayerGroup>
            <WMSTileLayer
              url="https://ows.emodnet-bathymetry.eu/wms"
              layers="emodnet:mean_multicolour"
              format="image/png"
              transparent={true}
              opacity={0.7}
              version="1.1.1"
              noWrap={true}
              bounds={[[-85, -180], [85, 180]]}
              crossOrigin="anonymous"
              pane="bathymetryPane"
              zIndex={354}
            />
          </LayerGroup>
        </Overlay>

        <Overlay name={EMODNET_CONTOURS_LAYER_NAME}>
          <LayerGroup>
            <WMSTileLayer
              url="https://ows.emodnet-bathymetry.eu/wms"
              layers="emodnet:contours"
              format="image/png"
              transparent={true}
              version="1.1.1"
              noWrap={true}
              bounds={[[-85, -180], [85, 180]]}
              crossOrigin="anonymous"
              pane="bathymetryPane"
              zIndex={355}
            />
          </LayerGroup>
        </Overlay>
      </LayersControl>

      <MapLayerControlActivity
        onBaseLayerChange={setActiveBaseLayer}
        onBathymetryLayerActiveChange={setBathymetryLayerActive}
      />
      <BathymetryAttribution attribution={bathymetryAttribution} />

      <MobileMapLayersSheet
        isOpen={mobileLayersOpen}
        onClose={() => setMobileLayersOpen(false)}
        baseLayers={baseLayerOptions}
        overlayLayers={overlayLayerOptions}
        activeBaseLayer={activeBaseLayer}
        activeBathymetryLayers={activeBathymetryLayers}
        onSelectLayer={selectLeafletControlLayer}
      />

      {isBathymetryActive && (
        <div className="map-bathymetry-warning" role="status">
          {BATHYMETRY_WARNING}
        </div>
      )}

      <MapMeasurementTool
        isMeasuring={isMeasuring}
        measurementPoints={measurementPoints}
        onToggleMeasuring={toggleMeasuring}
        onClearMeasurement={clearMeasurement}
        onExitMeasuring={exitMeasuring}
      />

      <MapRefSetter mapRef={map.mapRef} />

      <MapMarkers
        posts={map.posts}
        backendCatches={map.filteredCatchMarkers}
        mapSpots={map.filteredMapSpots}
        marker={map.marker}
        onMapClick={handleMapCanvasClick}
        viewer={map.viewer}
        onBasinClick={handleBasinClick}
        onSpotClick={handleSpotClick}
      />
    </LeafletMapContainer>
  );
}
